import React, { FC } from 'react';

import Input from 'components/Input/Input';
import RadioButtonGroup from 'components/RadioButton/RadioButtonGroup';
import RichText from 'components/RichText/RichText';
import Switch from 'components/Switch/Switch';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';
import { ColorPicker } from 'components/ui/ColorPicker/color-picker';
import UploadImage from 'components/UploadImage/UploadImage';

import { MENU_RADIO_BUTTON_OPTIONS } from './constants';
import { DesignLayoutSettingsProps } from './types';

const DesignLayoutSettings: FC<DesignLayoutSettingsProps> = ({
  control,
  errors,
  hasFeatureFlagShowLoginButton
}) => {
  return (
    <>
      <div className="border border-base-menuBorder rounded-[20px] mb-5 p-9">
        <RichText
          title="General Settings"
          description="Manage your portal's identity and appearance"
          className="mb-9"
        />
        <div className="grid grid-cols-2 gap-9 w-[586px]">
          <FormField
            name="generalBackgroundColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="General background color"
                    data-testid="PortalTheme-General-BackgroundColor"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="generalSectionTextColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Section text color"
                    data-testid="PortalTheme-SectionTextColor"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="mt-9 w-[586px]">
          <FormField
            name="generalFavicon"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <div data-testid="PortalTheme-General-FaviconUpload">
                    <UploadImage
                      className="w-full"
                      title="Add a favicon *"
                      description={`Add a favicon to your page`}
                      buttonText="Upload"
                      data-testid="PortalTheme-General-FaviconUpload"
                      selectedImageUrl={value}
                      onUpload={(url: string) => {
                        onChange(url);
                      }}
                    />
                  </div>
                </FormControl>
                <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
              </FormItem>
            )}
          />
        </div>
        <div className="mt-9 w-[586px]">
          <FormField
            name="homePageBanner"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <div data-testid="PortalTheme-HomePageBannerUpload">
                    <UploadImage
                      className="w-full"
                      title="Home Page Banner Image *"
                      description="The image will be scaled to 130px x 60px"
                      buttonText="Upload"
                      selectedImageUrl={value}
                      onUpload={(url: string) => {
                        onChange(url);
                      }}
                    />
                  </div>
                </FormControl>
                <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
              </FormItem>
            )}
          />
        </div>
      </div>

      <div className="border border-base-menuBorder rounded-[20px] mb-5 p-9">
        <RichText
          title="Header"
          description="Customize your portal's header"
          className="mb-9 [&>h4]:text-p-l [&>h4]:font-semibold"
        />
        <div className="flex flex-col w-[586px]">
          <div className="mb-9">
            <FormField
              name="headerLogo"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <div data-testid="PortalTheme-HeaderLogoUpload">
                      <UploadImage
                        className="w-full"
                        title="Add a logo"
                        description={`Add a logo to your portal.\nThe image will be scaled to 130px x 60px`}
                        buttonText="Upload"
                        selectedImageUrl={value}
                        onUpload={(url: string) => {
                          onChange(url);
                        }}
                      />
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-3 mb-9 gap-9">
            <FormField
              name="headerBackgroundColor"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <ColorPicker
                      label="Background color"
                      data-testid="PortalTheme-Header-BackgroundColor"
                      onChange={onChange}
                      defaultValue={value}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              name="headerTextColor"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <ColorPicker
                      label="Text color"
                      data-testid="PortalTheme-Header-TextColor"
                      onChange={onChange}
                      defaultValue={value}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-p-s text-base-destructive" />
                </FormItem>
              )}
            />
            <FormField
              name="headerLoginButton.backgroundColor"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <ColorPicker
                      label="Login Button"
                      data-testid="PortalTheme-Header-LoginButton"
                      onChange={onChange}
                      defaultValue={value}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-1 gap-y-9">
            <FormField
              name="headerIsPlayerNameShown"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Switch
                      label="Show player name"
                      data-testid="PortalTheme-Header-PlayerName"
                      description="Display the logged-in player's name in the header"
                      textAlignment="right"
                      defaultChecked={value}
                      size="lg"
                      onCheckedChange={onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              name="headerIsPlayerBalanceShown"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Switch
                      label="Show player balance"
                      data-testid="PortalTheme-Header-PlayerBalance"
                      description="Display the logged-in player's account balance in the header"
                      textAlignment="right"
                      defaultChecked={value}
                      size="lg"
                      onCheckedChange={onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {hasFeatureFlagShowLoginButton && (
              <FormField
                name="headerIsLoginBtnShown"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <Switch
                        label="Show login button"
                        data-testid="PortalTheme-Header-LoginBtn"
                        description="Display a login button in the header"
                        textAlignment="right"
                        defaultChecked={value}
                        size="lg"
                        onCheckedChange={onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
            <FormField
              name="headerIsLanguageSwitcherShown"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Switch
                      label="Show language switcher"
                      data-testid="PortalTheme-Header-LanguageSwitcher"
                      description="Display a dropdown menu for allowing players to change the portal's language"
                      textAlignment="right"
                      defaultChecked={value}
                      size="lg"
                      onCheckedChange={onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
      <div className="border border-base-menuBorder rounded-[20px] mb-5 p-9">
        <RichText
          title="Content"
          description="Customize the look of article/news used in your content pages"
          className="mb-9"
        />
        <div className="grid grid-cols-2 w-[586px] xl:grid-cols-3 mb-9 gap-9 xl:w-[897px]">
          <FormField
            name="contentCardBackgroundColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Card background color"
                    data-testid="PortalTheme-ContentCardBackgroundColor"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="contentCardTextColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Card text color"
                    data-testid="PortalTheme-ContentCardTextColor"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 w-[586px] xl:grid-cols-3 gap-9 xl:w-[897px]">
          <FormField
            name="contentCardButtonColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Card button color"
                    data-testid="PortalTheme-ContentCardButtonColor"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="contentPageTextColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Content body text color"
                    data-testid="PortalTheme-ContentPageTextColor"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
      <div className="border border-base-menuBorder rounded-[20px] mb-5 p-9">
        <RichText
          title="Menu"
          description="Customize your portal's menu"
          className="mb-6"
        />
        <div className="mb-9">
          <FormField
            name="menuDesktopNavigationLayout"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <RadioButtonGroup
                    value={value}
                    onValueChange={onChange}
                    options={MENU_RADIO_BUTTON_OPTIONS}
                    className="gap-x-9"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 w-[586px] gap-9">
          <FormField
            name="menuBackgroundColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Background color"
                    data-testid="PortalTheme-SidebarBackgroundColor"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="menuTextColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Text color"
                    data-testid="PortalTheme-SidebarTextColor"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="menuHoverColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Tab on-hover color"
                    data-testid="PortalTheme-SidebarHoverColor"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="selectedTabColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Selected Tab Color"
                    data-testid="PortalTheme-SelectedTabColor"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
      <div className="border border-base-menuBorder rounded-[20px] mb-0 p-9">
        <RichText
          title="Footer"
          description="Customize your portal's footer"
          className="mb-9"
        />
        <div className="grid grid-cols-2 mb-9 gap-9 w-[586px]">
          <FormField
            name="footerBackgroundColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Background color"
                    data-testid="PortalTheme-Footer-BackgroundColor"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="footerTextColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Text color"
                    data-testid="PortalTheme-Footer-TextColor"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-1 gap-9 w-[586px]">
          <FormField
            name="footerPrivacyLink"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <Input
                    label="Privacy link"
                    data-testid="PortalTheme-Footer-PrivacyLink"
                    placeholder="http://"
                    value={value}
                    onChange={onChange}
                    state={
                      errors.footerPrivacyLink?.message ? 'error' : 'default'
                    }
                    description={errors.footerPrivacyLink?.message}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="footerTermsConditionsLink"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <Input
                    label="Terms & Conditions link"
                    data-testid="PortalTheme-Footer-TermsConditionsLink"
                    placeholder="http://"
                    value={value}
                    onChange={onChange}
                    state={
                      errors.footerTermsConditionsLink?.message
                        ? 'error'
                        : 'default'
                    }
                    description={errors.footerTermsConditionsLink?.message}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="footerSupportLink"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <Input
                    label="Support link"
                    data-testid="PortalTheme-Footer-SupportLink"
                    placeholder="http://"
                    value={value}
                    onChange={onChange}
                    state={
                      errors.footerSupportLink?.message ? 'error' : 'default'
                    }
                    description={errors.footerSupportLink?.message}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
    </>
  );
};

export default DesignLayoutSettings;
